import React from "react";
import InfoIcon from "@mui/icons-material/Info";
import {
  Tooltip,
  styled,
  tooltipClasses,
  Paper,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import { format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import "./FaltaInfoTooltip.scss";
// Estilizando o tooltip para ter aparência customizada
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
}));

// Mapeamento dos tipos de motivo para exibição legível
const MOTIVOS_FALTA_LEGIVEL = {
  AGENDAMENTO_CANCELADO: "Agendamento cancelado",
  CONDIÇÕES_CLIMÁTICAS: "Condições climáticas",
  DOENÇA: "Doença",
  EMERGÊNCIA_FAMILIAR: "Emergência familiar",
  ESQUECIMENTO: "Esquecimento",
  MELHORA_DOS_SINTOMAS: "Melhora dos sintomas",
  PROBLEMAS_FINANCEIROS: "Problemas financeiros",
  SEM_CONTACTO: "Sem contacto",
  TRANSPORTE: "Problemas de transporte",
  OUTRO: "Outro motivo",
};

// Cores para os chips de motivo
const MOTIVO_COLORS = {
  AGENDAMENTO_CANCELADO: "#ffb74d", // laranja
  CONDIÇÕES_CLIMÁTICAS: "#90caf9", // azul claro
  DOENÇA: "#ef5350", // vermelho
  EMERGÊNCIA_FAMILIAR: "#ce93d8", // roxo
  ESQUECIMENTO: "#fff176", // amarelo
  MELHORA_DOS_SINTOMAS: "#81c784", // verde
  PROBLEMAS_FINANCEIROS: "#9e9e9e", // cinza
  SEM_CONTACTO: "#ff8a65", // coral
  TRANSPORTE: "#4fc3f7", // azul
  OUTRO: "#e0e0e0", // cinza claro
};

const FaltaInfoTooltip = ({ estatisticasFaltas }) => {
  // Formatação da data para exibição
  const formatarData = (dataString) => {
    try {
      const data = new Date(dataString);
      return format(data, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch (e) {
      return "Data inválida";
    }
  };

  // Verifica se não há faltas
  // if (
  //   !estatisticasFaltas ||
  //   !estatisticasFaltas.totalFaltas ||
  //   estatisticasFaltas.totalFaltas === 0
  // ) {
  //   return null;
  // }

  return (
    <span className="faltaInfoTooltip">
      <span>{estatisticasFaltas.totalFaltas}</span>
      <HtmlTooltip
        title={
          <Paper elevation={0} sx={{ p: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              Estatísticas de Faltas
            </Typography>

            {/* Resumo geral */}
            <div style={{ marginBottom: "12px" }}>
              <Typography variant="body2">
                <strong>Total de faltas:</strong>{" "}
                {estatisticasFaltas.totalFaltas}
              </Typography>
              <Typography variant="body2">
                <strong>Total de agendamentos:</strong>{" "}
                {estatisticasFaltas.totalAgendamentos}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color:
                    estatisticasFaltas.percentagemFaltasTotal > 25
                      ? "#d32f2f"
                      : "inherit",
                }}
              >
                Taxa de faltas: {estatisticasFaltas.percentagemFaltasTotal}%
              </Typography>
            </div>

            <Divider sx={{ my: 1 }} />

            {/* Motivos das faltas */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", mt: 1, mb: 1 }}
            >
              Motivos de Faltas
            </Typography>

            {estatisticasFaltas.tiposFaltas.map((tipoFalta, index) => (
              <div
                key={index}
                style={{
                  marginBottom: "12px",
                  paddingBottom:
                    index !== estatisticasFaltas.tiposFaltas.length - 1
                      ? "8px"
                      : 0,
                  borderBottom:
                    index !== estatisticasFaltas.tiposFaltas.length - 1
                      ? "1px dashed #eee"
                      : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "4px",
                  }}
                >
                  <Chip
                    label={tipoFalta.motivo}
                    size="small"
                    style={{
                      backgroundColor:
                        MOTIVO_COLORS[tipoFalta.codigo] || "#e0e0e0",
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {tipoFalta.quantidade} ({tipoFalta.percentagem}%)
                  </Typography>
                </div>

                {/* Exemplos de faltas recentes deste tipo */}
                {tipoFalta.detalhes &&
                  tipoFalta.detalhes.length > 0 &&
                  tipoFalta.detalhes.slice(0, 2).map((detalhe, idx) => (
                    <div
                      key={idx}
                      style={{
                        marginLeft: "8px",
                        fontSize: "11px",
                        marginTop: "4px",
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        • {formatarData(detalhe.data)} - {detalhe.procedimento}
                        {detalhe.detalhe && (
                          <span style={{ fontStyle: "italic" }}>
                            {" "}
                            - "{detalhe.detalhe.substring(0, 40)}
                            {detalhe.detalhe.length > 40 ? "..." : ""}"
                          </span>
                        )}
                      </Typography>
                    </div>
                  ))}

                {tipoFalta.detalhes && tipoFalta.detalhes.length > 2 && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "#666",
                      marginLeft: "8px",
                      marginTop: "2px",
                    }}
                  >
                    • E mais {tipoFalta.detalhes.length - 2} ocorrência(s)...
                  </Typography>
                )}
              </div>
            ))}
          </Paper>
        }
      >
        <InfoIcon
          fontSize="small"
          color={
            estatisticasFaltas.totalFaltas === 0
              ? "success"
              : estatisticasFaltas.percentagemFaltasTotal < 10
              ? "info"
              : "error"
          }
          sx={{
            ml: 1,
            cursor: "pointer",
            "&:hover": {
              color:
                estatisticasFaltas.totalFaltas === 0
                  ? "green"
                  : estatisticasFaltas.percentagemFaltasTotal < 10
                  ? "blue"
                  : "crimson",
            },
          }}
        />
      </HtmlTooltip>
    </span>
  );
};

export default FaltaInfoTooltip;
